import { BASE_API_URL } from '../apiConfig';
import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { SPORT_ROLES, getRoleBadge } from '../utils/sports';
import SlotCard from '../components/SlotCard';
import ConfirmationModal from '../components/ConfirmationModal';
import { useTranslation } from 'react-i18next';
import { subscribeUserToPush } from '../services/notificationService';

const LeagueDetail = () => {
    const { leagueId } = useParams();
    const { t } = useTranslation();
    const [slots, setSlots] = useState([]);
    const [matches, setMatches] = useState([]);
    const [isAdmin, setIsAdmin] = useState(false);
    const [memberStatus, setMemberStatus] = useState('none');
    const [leagueNotificationsEnabled, setLeagueNotificationsEnabled] = useState(true);
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [league, setLeague] = useState(null);
    const [leagueModalOpen, setLeagueModalOpen] = useState(false);
    const [editLeagueName, setEditLeagueName] = useState('');
    const [editLeaguePublic, setEditLeaguePublic] = useState(false);
    const [editSport, setEditSport] = useState('football');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalAction, setModalAction] = useState(null);
    const [selectedSlotId, setSelectedSlotId] = useState(null);
    const [selectedProposalId, setSelectedProposalId] = useState(null);
    const [showPastMatches, setShowPastMatches] = useState(false);
    const [joinInviteCode, setJoinInviteCode] = useState('');
    const [joinLevel, setJoinLevel] = useState(3);
    const [joinRoles, setJoinRoles] = useState([]);
    const [alertModalConfig, setAlertModalConfig] = useState({ isOpen: false, title: '', message: '' });
    const [inviteModalOpen, setInviteModalOpen] = useState(false);
    const [collapsedMatchIds, setCollapsedMatchIds] = useState(new Set());
    const initializedRef = React.useRef(false); // Per inizializzare expand/collapse solo una volta
    const dataLoadedRef = React.useRef(false); // Per caricare i dati della lega nei form solo una volta
    const [editSlotModalOpen, setEditSlotModalOpen] = useState(false);
    const [slotToEdit, setSlotToEdit] = useState(null);
    const [profileModalOpen, setProfileModalOpen] = useState(false);
    const [editProfileLevel, setEditProfileLevel] = useState(3);
    const [editProfileRoles, setEditProfileRoles] = useState([]);
    const editSlotModalOpenRef = React.useRef(false);
    const leagueModalOpenRef = React.useRef(false);
    const profileModalOpenRef = React.useRef(false);
    const teamModalOpenRef = React.useRef(false);
    const [previousLocations, setPreviousLocations] = useState([]);
    const [teamModalOpen, setTeamModalOpen] = useState(false);
    const [teamPlayer, setTeamPlayer] = useState(null);
    const [teamMatchId, setTeamMatchId] = useState(null);
    const [editSuggestionOpen, setEditSuggestionOpen] = useState(false);
    const [externalModalOpen, setExternalModalOpen] = useState(false);
    const [externalPlayerName, setExternalPlayerName] = useState('');
    const [externalPlayerLevel, setExternalPlayerLevel] = useState(3);
    const [externalPlayerSlotId, setExternalPlayerSlotId] = useState(null);
    const externalModalOpenRef = React.useRef(false);
    const [rebalanceModalOpen, setRebalanceModalOpen] = useState(false);
    const [rebalanceMatchId, setRebalanceMatchId] = useState(null);
    const rebalanceModalOpenRef = React.useRef(false);
    const [scoutingModalOpen, setScoutingModalOpen] = useState(false);
    const [scoutingSlotId, setScoutingSlotId] = useState(null);
    const [scoutingPlayers, setScoutingPlayers] = useState(1);
    const [scoutingTimeout, setScoutingTimeout] = useState(60);
    const [scoutingLevel, setScoutingLevel] = useState(0); // 0 = Automatic
    const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
    const [selectedScout, setSelectedScout] = useState(null);
    const [feedbackData, setFeedbackData] = useState({ wasPresent: true, rating: 5, comment: '' });
    const [scoutingCandidates, setScoutingCandidates] = useState({}); // { slotId: [candidates] }

    // Sincronizziamo i ref con gli stati per l'uso dentro setInterval (closure stale)
    React.useEffect(() => { editSlotModalOpenRef.current = editSlotModalOpen; }, [editSlotModalOpen]);
    React.useEffect(() => { leagueModalOpenRef.current = leagueModalOpen; }, [leagueModalOpen]);
    React.useEffect(() => { profileModalOpenRef.current = profileModalOpen; }, [profileModalOpen]);
    React.useEffect(() => { teamModalOpenRef.current = teamModalOpen; }, [teamModalOpen]);
    React.useEffect(() => { externalModalOpenRef.current = externalModalOpen; }, [externalModalOpen]);
    React.useEffect(() => { rebalanceModalOpenRef.current = rebalanceModalOpen; }, [rebalanceModalOpen]);

    const navigate = useNavigate();
    const location = useLocation();

    const handleEditSlot = (slotId) => {
        const slot = slots.find(s => s.id === slotId) || matches.find(m => m.id === slotId);
        if (slot) {
            // Se non c'è il prezzo, proviamo a cercarlo nello storico per questa combinazione
            let currentPrice = slot.price;
            if (!currentPrice) {
                const historyMatch = previousLocations.find(loc =>
                    loc.location.toLowerCase() === slot.location?.toLowerCase() &&
                    Number(loc.max_players) === Number(slot.max_players)
                );
                if (historyMatch) currentPrice = historyMatch.price;
            }

            // Usiamo locale per datetime-local, ISO toglie l'offset orario rischiando di cambiare ora
            const date = new Date(slot.start_time);
            const tzOffset = date.getTimezoneOffset() * 60000;
            const localISOTime = new Date(date.getTime() - tzOffset).toISOString().slice(0, 16);

            setSlotToEdit({
                ...slot,
                startTime: localISOTime,
                maxPlayers: slot.max_players,
                price: currentPrice
            });
            setEditSlotModalOpen(true);
        }
    };

    const handleEditSelectSuggestion = (loc) => {
        const matchingHistory = previousLocations.find(p =>
            p.location === loc.location &&
            Number(p.max_players) === Number(slotToEdit.maxPlayers)
        );

        setSlotToEdit({
            ...slotToEdit,
            location: loc.location,
            address: loc.address || '',
            lat: loc.lat || null,
            lng: loc.lng || null,
            price: matchingHistory ? matchingHistory.price : slotToEdit.price
        });
        setEditSuggestionOpen(false);
    };

    const handleEditFieldUpdate = (field, value) => {
        setSlotToEdit(prev => {
            const newState = { ...prev, [field]: value || null };

            // Logic for price auto-completion based on history
            if (field === 'location' || field === 'maxPlayers') {
                const locName = field === 'location' ? value : prev.location;
                const players = field === 'maxPlayers' ? value : prev.maxPlayers;

                if (locName) {
                    const historyMatch = previousLocations.find(l =>
                        l.location.toLowerCase() === locName.toLowerCase() &&
                        Number(l.max_players) === Number(players)
                    );
                    if (historyMatch && historyMatch.price) {
                        newState.price = historyMatch.price;
                    }
                }
            }
            return newState;
        });
    };

    const loadData = async (isBackground = false) => {
        // Se è un refresh automatico in background, evitiamo di ricaricare se l'utente sta scrivendo in una modale
        if (isBackground && (editSlotModalOpenRef.current || leagueModalOpenRef.current || profileModalOpenRef.current || teamModalOpenRef.current || externalModalOpenRef.current)) return;

        const token = localStorage.getItem('token');
        if (!token) {
            localStorage.setItem('redirectAfterLogin', location.pathname + location.search);
            navigate('/login');
            return;
        }

        // Mostriamo lo stato caricamento solo alla prima volta, non nei background refresh
        if (!isBackground && !initializedRef.current) {
            setLoading(true);
        }
        try {
            // 1. Carichiamo gli slot/proposte
            const res = await fetch(`${BASE_API_URL}/leagues/${leagueId}/proposals`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();

            // Calcoliamo la priorità tra quelle che hanno raggiunto il quorum
            const readyProposals = data
                .filter(s => s.reached_min_at !== null)
                .sort((a, b) => new Date(a.reached_min_at) - new Date(b.reached_min_at));

            const slotsWithPriority = (Array.isArray(data) ? data : []).map(s => {
                if (s.reached_min_at === null) return s;
                const rank = readyProposals.findIndex(rp => rp.id === s.id) + 1;
                return { ...s, priority_rank: rank };
            });

            const sortedSlots = slotsWithPriority.sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
            setSlots(sortedSlots);

            // 2. Carichiamo i match confermati
            const matchRes = await fetch(`${BASE_API_URL}/leagues/${leagueId}/matches`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const matchData = await matchRes.json();
            const matchesArr = Array.isArray(matchData) ? matchData : [];
            setMatches(matchesArr);

            // Carica candidati per match scouting attivi
            const activeScoutingMatches = matchesArr.filter(m => !m.is_past && !m.is_voting);
            const candidatesMap = {};
            for (const match of activeScoutingMatches) {
                const cRes = await fetch(`${BASE_API_URL}/matches/${match.id}/scouting-candidates`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (cRes.ok) {
                    candidatesMap[match.id] = await cRes.json();
                }
            }
            setScoutingCandidates(candidatesMap);

            // Inizializziamo i match collassati solo alla prima carica
            if (!initializedRef.current) {
                const newCollapsed = new Set();
                matchesArr.forEach(m => {
                    if (m.user_not_available) {
                        newCollapsed.add(m.id);
                    }
                });
                setCollapsedMatchIds(newCollapsed);
                initializedRef.current = true;
            }

            // 3. Verifichiamo il ruolo dell'utente
            const roleRes = await fetch(`${BASE_API_URL}/leagues/${leagueId}/role`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const roleData = await roleRes.json();
            setIsAdmin(roleData.is_admin);
            setMemberStatus(roleData.status);
            if (roleData.status === 'approved') {
                setEditProfileLevel(roleData.level || 3);
                setEditProfileRoles(roleData.roles || []);
                setLeagueNotificationsEnabled(roleData.notifications_enabled !== false);
            }

            if (roleData.is_admin) {
                const reqsRes = await fetch(`${BASE_API_URL}/leagues/${leagueId}/requests`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const reqsData = await reqsRes.json();
                setRequests(Array.isArray(reqsData) ? reqsData : []);

                // Storico location per autocompletamento in modifica
                const locsRes = await fetch(`${BASE_API_URL}/leagues/${leagueId}/locations`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (locsRes.ok) {
                    const locsData = await locsRes.json();
                    setPreviousLocations(locsData);
                }
            }

            // 4. Carichiamo i dettagli della lega
            const leagueRes = await fetch(`${BASE_API_URL}/leagues/${leagueId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const leagueData = await leagueRes.json();
            setLeague(leagueData);

            // Inizializziamo i dati del form di editing solo se non sono già stati caricati/toccati
            if (!dataLoadedRef.current) {
                setEditLeagueName(leagueData.name);
                setEditLeaguePublic(leagueData.is_public);
                setEditSport(leagueData.sport || 'football');
                dataLoadedRef.current = true;
            }

        } catch (err) {
            console.error("Errore caricamento:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleVote = (slotId) => {
        setSelectedSlotId(slotId);
        setModalAction('vote');
        setIsModalOpen(true);
    };

    const handleUnvote = (slotId) => {
        setSelectedSlotId(slotId);
        setModalAction('unvote');
        setIsModalOpen(true);
    };

    const handleRemoveBooking = (slotId) => {
        setSelectedSlotId(slotId);
        setModalAction('removeBooking');
        setIsModalOpen(true);
    };

    const handleAddBooking = (slotId) => {
        setSelectedSlotId(slotId);
        setModalAction('addBooking');
        setIsModalOpen(true);
    };

    const handleConfirmRequest = (slotId) => {
        setSelectedSlotId(slotId);
        setModalAction('confirm');
        setIsModalOpen(true);
    };

    const handleDeleteRequest = (proposalId, slotId = null) => {
        setSelectedProposalId(proposalId);
        setSelectedSlotId(slotId);
        setModalAction('deleteProposal');
        setIsModalOpen(true);
    };

    const handleCancelMatch = (slotId) => {
        setSelectedSlotId(slotId);
        setModalAction('cancelMatch');
        setIsModalOpen(true);
    };

    const handleNotAvailable = (slotId) => {
        setSelectedSlotId(slotId);
        setModalAction('not_available');
        setIsModalOpen(true);
    };

    const handleDeleteLeague = () => {
        setLeagueModalOpen(false); // Chiudi il modal impostazioni prima
        setTimeout(() => {
            setModalAction('deleteLeague');
            setIsModalOpen(true);
        }, 300);
    };

    const handleConfirm = async (reason) => {
        const token = localStorage.getItem('token');
        setIsModalOpen(false);

        try {
            if (modalAction === 'confirm') {
                const res = await fetch(`${BASE_API_URL}/leagues/${leagueId}/confirm-slot/${selectedSlotId}`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                    setAlertModalConfig({ isOpen: true, title: t('success'), message: t('match_confirmed') });
                    loadData();
                } else {
                    const errData = await res.json();
                    setAlertModalConfig({ isOpen: true, title: t('error'), message: errData.error || t('error_confirming_match') });
                }
            } else if (modalAction === 'deleteProposal') {
                const url = selectedSlotId
                    ? `${BASE_API_URL}/leagues/${leagueId}/proposals/${selectedProposalId}/slots/${selectedSlotId}`
                    : `${BASE_API_URL}/leagues/${leagueId}/proposals/${selectedProposalId}`;

                const res = await fetch(url, {
                    method: 'DELETE',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ reason })
                });
                if (res.ok) {
                    setAlertModalConfig({ isOpen: true, title: t('success'), message: selectedSlotId ? t('option_deleted') : t('proposal_deleted') });
                    setSelectedSlotId(null);
                    loadData();
                } else {
                    const errData = await res.json();
                    setAlertModalConfig({ isOpen: true, title: t('error'), message: errData.error || t('error_deleting_proposal') });
                }
            } else if (modalAction === 'cancelMatch') {
                const res = await fetch(`${BASE_API_URL}/leagues/${leagueId}/matches/${selectedSlotId}`, {
                    method: 'DELETE',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ reason })
                });
                if (res.ok) {
                    setAlertModalConfig({ isOpen: true, title: t('success'), message: t('event_cancelled_successfully') });
                    loadData();
                } else {
                    const errData = await res.json();
                    setAlertModalConfig({ isOpen: true, title: t('error'), message: errData.error || t('error_cancelling_event') });
                }
            } else if (modalAction === 'vote') {
                const res = await fetch(`${BASE_API_URL}/slots/${selectedSlotId}/vote`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const data = await res.json();
                if (res.ok) {
                    if (data.warning) {
                        setAlertModalConfig({ isOpen: true, title: "Sovrapposizione Orari", message: data.warning });
                    }
                    loadData();
                } else {
                    setAlertModalConfig({ isOpen: true, title: "Errore", message: data.error || "Errore durante il voto" });
                }
            } else if (modalAction === 'unvote') {
                const res = await fetch(`${BASE_API_URL}/slots/${selectedSlotId}/vote`, {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                    loadData();
                } else {
                    const errData = await res.json();
                    setAlertModalConfig({ isOpen: true, title: "Errore", message: errData.error || t('error_removing_vote') });
                }
            } else if (modalAction === 'not_available') {
                const res = await fetch(`${BASE_API_URL}/slots/${selectedSlotId}/not-available`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                    loadData();
                } else {
                    const errData = await res.json();
                    setAlertModalConfig({ isOpen: true, title: "Errore", message: errData.error || "Errore durante l'azione" });
                }
            } else if (modalAction === 'addBooking') {
                const res = await fetch(`${BASE_API_URL}/matches/${selectedSlotId}/booking`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const data = await res.json();
                if (res.ok) {
                    if (data.warning) {
                        setAlertModalConfig({ isOpen: true, title: "Sovrapposizione Orari", message: data.warning });
                    }
                    loadData();
                } else {
                    setAlertModalConfig({ isOpen: true, title: "Errore", message: data.error || "Errore durante l'aggiunta" });
                }
            } else if (modalAction === 'removeBooking') {
                const res = await fetch(`${BASE_API_URL}/matches/${selectedSlotId}/booking`, {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                    loadData();
                } else {
                    const errData = await res.json();
                    setAlertModalConfig({ isOpen: true, title: "Errore", message: errData.error || "Errore rimozione prenotazione" });
                }
            } else if (modalAction === 'deleteLeague') {
                const res = await fetch(`${BASE_API_URL}/leagues/${leagueId}`, {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                    navigate('/');
                } else {
                    const errData = await res.json();
                    setAlertModalConfig({ isOpen: true, title: "Errore", message: errData.error || "Errore eliminazione lega" });
                }
            }
        } catch (err) {
            console.error("Errore operazione:", err);
        }
    };

    const handleMaxPlayersChange = async (matchId, newMax) => {
        const token = localStorage.getItem('token');
        try {
            const res = await fetch(`${BASE_API_URL}/leagues/${leagueId}/matches/${matchId}/max-players`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ maxPlayers: newMax })
            });
            if (res.ok) {
                loadData();
            } else {
                setAlertModalConfig({ isOpen: true, title: "Errore", message: "Errore modifica partecipanti" });
            }
        } catch (err) {
            console.error(err);
        }
    };

    const handleTeamChange = async (matchId, userId, team) => {
        const token = localStorage.getItem('token');
        try {
            const res = await fetch(`${BASE_API_URL}/leagues/${leagueId}/matches/${matchId}/bookings/${userId}/team`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ team })
            });
            if (res.ok) {
                loadData();
            } else {
                setAlertModalConfig({ isOpen: true, title: t("error"), message: t("error_assigning_team") });
            }
        } catch (err) {
            console.error(err);
        }
    };

    const handleSaveLeagueInfo = async () => {
        const token = localStorage.getItem('token');
        try {
            const res = await fetch(`${BASE_API_URL}/leagues/${leagueId}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ name: editLeagueName, is_public: editLeaguePublic, sport: editSport })
            });
            if (res.ok) {
                setLeagueModalOpen(false);
                setTimeout(() => {
                    setAlertModalConfig({ isOpen: true, title: t('success'), message: t('league_info_updated') });
                }, 300);
                loadData();
            } else {
                setAlertModalConfig({ isOpen: true, title: t('error'), message: t('error_saving_league_info') });
            }
        } catch (err) {
            console.error(err);
        }
    };

    const copyToClipboard = (text, label) => {
        if (navigator.clipboard) {
            navigator.clipboard.writeText(text);
            setAlertModalConfig({ isOpen: true, title: "Copiato", message: `${label} copiato negli appunti!` });
        } else {
            // Fallback per ambienti non sicuri (http)
            const textArea = document.createElement("textarea");
            textArea.value = text;
            document.body.appendChild(textArea);
            textArea.select();
            try {
                document.execCommand('copy');
                setAlertModalConfig({ isOpen: true, title: "Copiato", message: `${label} copiato negli appunti (fallback)!` });
            } catch (err) {
                console.error('Fallback copy failed', err);
            }
            document.body.removeChild(textArea);
        }
    };

    const copyInviteAction = () => {
        setInviteModalOpen(true);
    };

    const handleRequestAction = async (userId, action) => {
        const token = localStorage.getItem('token');
        try {
            const res = await fetch(`${BASE_API_URL}/leagues/${leagueId}/requests/${userId}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ action })
            });
            if (res.ok) {
                loadData();
            }
        } catch (err) {
            console.error(err);
        }
    };

    const handleJoinLeague = async () => {
        const token = localStorage.getItem('token');
        try {
            const inviteToUse = league ? league.invite_code : joinInviteCode;
            const res = await fetch(`${BASE_API_URL}/leagues/join`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    inviteCode: inviteToUse,
                    level: joinLevel,
                    roles: joinRoles
                })
            });
            const data = await res.json();
            if (res.ok) {
                setAlertModalConfig({ isOpen: true, title: t('success'), message: data.message });
                setMemberStatus('pending'); // Aggiorna lo stato locale per mostrare il messaggio di attesa
                loadData();
            } else {
                setAlertModalConfig({ isOpen: true, title: t('error'), message: data.error });
            }
        } catch (err) {
            console.error(err);
        }
    };

    const handleUpdateProfile = async (e) => {
        if (e) e.preventDefault();
        const token = localStorage.getItem('token');
        try {
            const res = await fetch(`${BASE_API_URL}/leagues/${leagueId}/membership`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ roles: editProfileRoles })
            });
            if (res.ok) {
                setAlertModalConfig({ isOpen: true, title: t('success'), message: t('profile_updated') });
                setProfileModalOpen(false);
                loadData();
            }
        } catch (err) {
            console.error(err);
        }
    };

    const toggleMatchCollapse = (matchId) => {
        setCollapsedMatchIds(prev => {
            const next = new Set(prev);
            if (next.has(matchId)) next.delete(matchId);
            else next.add(matchId);
            return next;
        });
    };
    const handleUpdateSlot = async (e) => {
        e.preventDefault();
        const token = localStorage.getItem('token');
        try {
            // Convertiamo la data locale dell'input in ISO UTC in modo deterministico
            let submissionStartTime = null;
            if (slotToEdit.startTime) {
                const [datePart, timePart] = slotToEdit.startTime.split('T');
                const [y, m, d] = datePart.split('-');
                const [h, min] = timePart.split(':');
                submissionStartTime = new Date(y, m - 1, d, h, min).toISOString();
            }

            const submissionData = {
                startTime: submissionStartTime,
                maxPlayers: parseInt(slotToEdit.maxPlayers),
                location: slotToEdit.location,
                address: slotToEdit.address,
                lat: slotToEdit.lat,
                lng: slotToEdit.lng,
                price: slotToEdit.price !== null ? parseFloat(slotToEdit.price) : null
            };

            const res = await fetch(`${BASE_API_URL}/leagues/${leagueId}/matches/${slotToEdit.id}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(submissionData)
            });

            if (res.ok) {
                setAlertModalConfig({ isOpen: true, title: t("success"), message: t("match_updated") });
                setEditSlotModalOpen(false);
                loadData();
            } else {
                setAlertModalConfig({ isOpen: true, title: t("error"), message: data.error });
            }
        } catch (err) {
            console.error(err);
        }
    };

    const handleAddExternalPlayer = async (e) => {
        if (e) e.preventDefault();
        if (!externalPlayerName.trim()) return;

        const token = localStorage.getItem('token');
        try {
            const res = await fetch(`${BASE_API_URL}/leagues/${leagueId}/matches/${externalPlayerSlotId}/external-booking`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ name: externalPlayerName, level: externalPlayerLevel })
            });

            if (res.ok) {
                setExternalModalOpen(false);
                setExternalPlayerName('');
                setExternalPlayerLevel(3);
                loadData();
            }
        } catch (err) {
            console.error(err);
        }
    };


    const handleCreateScoutingRequest = async (e) => {
        if (e) e.preventDefault();
        const token = localStorage.getItem('token');
        try {
            const res = await fetch(`${BASE_API_URL}/leagues/${leagueId}/matches/${scoutingSlotId}/scouting`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ 
                    neededPlayers: scoutingPlayers, 
                    timeoutMinutes: scoutingTimeout,
                    level: scoutingLevel > 0 ? scoutingLevel : null
                })
            });

            if (res.ok) {
                setScoutingModalOpen(false);
                setAlertModalConfig({ isOpen: true, title: t('success'), message: t('scouting_success') });
                loadData();
            }
        } catch (err) {
            console.error(err);
        }
    };

    const handleSubmitFeedback = async (e) => {
        if (e) e.preventDefault();
        const token = localStorage.getItem('token');
        try {
            const res = await fetch(`${BASE_API_URL}/matches/${selectedScout.slotId}/scout/${selectedScout.user.user_id}/feedback`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(feedbackData)
            });

            if (res.ok) {
                setFeedbackModalOpen(false);
                setAlertModalConfig({ isOpen: true, title: t('success'), message: t('feedback_success') });
                loadData();
            }
        } catch (err) {
            console.error(err);
        }
    };

    const handleAcceptCandidate = async (candidateId) => {
        const token = localStorage.getItem('token');
        try {
            const res = await fetch(`${BASE_API_URL}/scouting-candidates/${candidateId}/accept`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                loadData();
            }
        } catch (err) {
            console.error(err);
        }
    };

    const handleRemoveExternalPlayer = async (mId, bId) => {
        if (!window.confirm("Rimuovere questo iscritto esterno?")) return;
        const token = localStorage.getItem('token');
        try {
            const res = await fetch(`${BASE_API_URL}/leagues/${leagueId}/matches/${mId}/bookings/${bId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) loadData();
        } catch (err) {
            console.error(err);
        }
    };
    const handleToggleTeamsLock = async (slotId, locked) => {
        const token = localStorage.getItem('token');
        try {
            const res = await fetch(`${BASE_API_URL}/leagues/${leagueId}/matches/${slotId}/lock-teams`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ locked })
            });

            if (res.ok) {
                loadData();
            }
        } catch (err) {
            console.error(err);
        }
    };

    const handleOpenTeamModal = (mId, p) => {
        if (!isAdmin) return;
        setTeamMatchId(mId);
        setTeamPlayer(p);
        if (p.is_external) {
            setExternalPlayerName(p.external_name);
            setExternalPlayerLevel(p.level || 3);
            setExternalPlayerSlotId(mId);
        }
        setTeamModalOpen(true);
    };

    const handleUpdateExternalPlayer = async () => {
        if (!externalPlayerName.trim()) return;
        const token = localStorage.getItem('token');
        try {
            const res = await fetch(`${BASE_API_URL}/leagues/${leagueId}/matches/${teamMatchId}/bookings/${teamPlayer.booking_id}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ name: externalPlayerName, level: externalPlayerLevel })
            });

            if (res.ok) {
                setTeamModalOpen(false);
                loadData();
            }
        } catch (err) {
            console.error(err);
        }
    };

    const handleSetPlayerTeam = async (team) => {
        if (!teamMatchId || !teamPlayer) return;
        const playerId = teamPlayer.user_id || teamPlayer.booking_id;
        await handleTeamChange(teamMatchId, playerId, team);
        setTeamModalOpen(false);
    };

    const handleToggleLeagueNotifications = async () => {
        const next = !leagueNotificationsEnabled;

        if (next && window.Notification && Notification.permission !== 'granted') {
            await subscribeUserToPush();
        }

        const token = localStorage.getItem('token');
        try {
            const res = await fetch(`${BASE_API_URL}/leagues/${leagueId}/notifications`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ enabled: next })
            });
            if (res.ok) {
                setLeagueNotificationsEnabled(next);
            }
        } catch (err) { }
    };

    const handleToggleMasterPush = async (val) => {
        const token = localStorage.getItem('token');
        try {
            const res = await fetch(`${BASE_API_URL}/leagues/${leagueId}/push-master`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ enabled: val })
            });
            if (res.ok) {
                setLeague(prev => ({ ...prev, push_notifications_enabled: val }));
            }
        } catch (err) { }
    };

    useEffect(() => {
        if (window.Notification && Notification.permission === 'granted') {
            subscribeUserToPush();
        }
        loadData();

        // Controllo se c'è un codice invito nella URL
        const params = new URLSearchParams(window.location.search);
        const code = params.get('code');
        if (code) {
            setJoinInviteCode(code);
        }

        const intervalId = setInterval(() => {
            loadData(true);
        }, 15000);
        return () => clearInterval(intervalId);
    }, [leagueId]);

    const getUserIdFromToken = () => {
        const token = localStorage.getItem('token');
        if (!token) return null;
        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            return payload.id;
        } catch (e) {
            return null;
        }
    };
    const loggedUserId = getUserIdFromToken();

    const calculateDistance = (lat1, lon1, lat2, lon2) => {
        if (!lat1 || !lon1 || !lat2 || !lon2) return null;
        const R = 6371; // km
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLon = (lon2 - lon1) * Math.PI / 180;
        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return (R * c).toFixed(1);
    };

    if (loading) return <div>{t('loading')}</div>;

    if (memberStatus === 'none' && (!league || !league.is_public)) {
        return (
            <div className="league-detail-page">
                <div className="card empty-state" style={{ maxWidth: '500px', margin: '100px auto' }}>
                    <h2 style={{ color: '#d32f2f' }}>🔒 {t('private_league')}</h2>
                    <p style={{ marginBottom: '20px' }}>{t('no_access_request')}</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <input
                            value={joinInviteCode}
                            onChange={(e) => setJoinInviteCode(e.target.value)}
                            placeholder={t('invite_code_placeholder')}
                        />

                        <div style={{ display: 'flex', gap: '10px' }}>
                            {/* Livello rimosso per i partecipanti */}
                            {league && (
                                <div style={{ flex: 1 }}>
                                    <label style={{ fontSize: '0.8rem', color: '#666', display: 'block', marginBottom: '4px' }}>{t('your_roles')}</label>
                                    <div style={{ padding: '8px', background: '#fff', border: '1px solid #ddd', borderRadius: '4px', maxHeight: '80px', overflowY: 'auto', fontSize: '0.8rem' }}>
                                        {(SPORT_ROLES[league.sport] || []).map(r => (
                                            <label key={r} style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer', marginBottom: '2px' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={joinRoles.includes(r)}
                                                    onChange={e => {
                                                        const next = e.target.checked
                                                            ? [...joinRoles, r]
                                                            : joinRoles.filter(role => role !== r);
                                                        setJoinRoles(next);
                                                    }}
                                                />
                                                {t(r)}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        <button onClick={handleJoinLeague} className="primary-btn" disabled={!joinInviteCode}>{t('send_access_request')}</button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="league-detail-page">
            <header className="page-header" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {league && (
                    <>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '15px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                                <h1 style={{ margin: 0 }}>{league.name}</h1>
                                {isAdmin && (
                                    <button
                                        onClick={() => setLeagueModalOpen(true)}
                                        className="icon-btn"
                                        style={{ background: 'transparent', border: 'none', fontSize: '1.2rem', cursor: 'pointer', padding: '5px' }}
                                        title={t('league_settings')}
                                    >
                                        🛠️
                                    </button>
                                )}
                                {memberStatus === 'approved' && league?.push_notifications_enabled && (
                                    <button
                                        onClick={handleToggleLeagueNotifications}
                                        className="icon-btn"
                                        style={{
                                            background: 'transparent',
                                            border: 'none',
                                            fontSize: '1.2rem',
                                            cursor: 'pointer',
                                            padding: '5px',
                                            filter: leagueNotificationsEnabled ? 'none' : 'grayscale(100%) opacity(0.6)'
                                        }}
                                        title={leagueNotificationsEnabled ? t('notifications_on') : t('notifications_off')}
                                    >
                                        {leagueNotificationsEnabled ? '🔔' : '🔕'}
                                    </button>
                                )}
                            </div>
                            <button onClick={copyInviteAction} className="outline-btn" style={{ whiteSpace: 'nowrap', marginTop: '5px' }}>
                                📋 {t('invite')}
                            </button>
                        </div>
                        <div className="riepilogo" style={{ display: 'flex', gap: '20px', color: '#666', flexWrap: 'wrap', alignItems: 'center' }}>
                            <span>{t('type')}: <strong className={`badge-${league.sport || 'football'}`} style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '0.8rem' }}>{league?.sport === 'football' ? '⚽ ' + t('football') : league?.sport === 'volleyball' ? '🏐 ' + t('volleyball') : league?.sport === 'padel' ? '🎾 ' + t('padel') : league?.sport === 'basketball' ? '🏀 ' + t('basketball') : league?.sport === 'tennis' ? '🎾 ' + t('tennis') : league?.sport === 'waterpolo' ? '🤽 ' + t('waterpolo') : '🏉 ' + t('rugby')}</strong></span>
                            <span>👥 {t('members')}: <strong>{league.members_count}</strong></span>
                            <span>{t('visibility')}: <strong>{league.is_public ? t('public') : t('private')}</strong></span>
                        </div>
                    </>
                )}
            </header>

            {memberStatus === 'none' && league && league.is_public && (
                <div className="card" style={{ background: '#e3f2fd', borderColor: '#90caf9', color: '#0d47a1', margin: '20px 0' }}>
                    <p style={{ margin: 0, fontWeight: 'bold' }}>{t('viewing_public_league')}</p>
                    <button onClick={handleJoinLeague} className="primary-btn" style={{ marginTop: '10px' }}>{t('join_to_participate')}</button>
                </div>
            )}

            {memberStatus === 'pending' && (
                <div className="card" style={{ background: '#fff3cd', borderColor: '#ffeeba', color: '#856404', margin: '20px 0' }}>
                    <p style={{ margin: 0, fontWeight: 'bold' }}>{t('pending_request')}</p>
                </div>
            )}

            {memberStatus === 'approved' && (
                <div className="card" style={{ marginBottom: '20px', marginTop: '10px', background: 'linear-gradient(to right, #f8f9fa, #fff)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 20px', borderLeft: '4px solid #1976d2', boxShadow: 'var(--shadow-sm)' }}>
                    <div>
                        <span style={{ fontSize: '0.7rem', color: '#888', letterSpacing: '1px', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>{t('your_league_profile')}</span>
                        <span style={{ fontSize: '0.8rem' }}>
                            <strong style={{ color: '#333' }}>{editProfileRoles && editProfileRoles.length > 0 ? editProfileRoles.map(r => t(r)).join(', ') : t('roles_unassigned')}</strong>
                        </span>
                    </div>
                    <button className="primary-btn outline" style={{ padding: '6px 14px', fontSize: '0.75rem' }} onClick={() => setProfileModalOpen(true)}>
                        ⚙️ {t('manage')}
                    </button>
                </div>
            )}

            {isAdmin && requests.length > 0 && (
                <section className="admin-section card" style={{ borderColor: '#ffc107', background: '#fffcf2' }}>
                    <div className="admin-badge" style={{ background: '#ffc107', color: '#000' }}>{t('requests')}</div>
                    <h3>👋 {t('new_participants')}</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '15px' }}>
                        {requests.map(req => (
                            <div key={req.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'white', padding: '10px', borderRadius: '4px', border: '1px solid #ddd' }}>
                                <span>{req.first_name} {req.last_name} ({req.email})</span>
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <button onClick={() => handleRequestAction(req.id, 'approve')} className="primary-btn responsive-btn" style={{ padding: '5px 10px' }}>
                                        ✓ <span className="btn-label">{t('approve')}</span>
                                    </button>
                                    <button onClick={() => handleRequestAction(req.id, 'reject')} className="danger-btn outline responsive-btn" style={{ padding: '5px 10px' }}>
                                        ✗ <span className="btn-label">{t('reject')}</span>
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* SEZIONE ADMIN: Solo il tasto per creare proposte */}
            {isAdmin && (
                <div style={{ margin: '15px 0 0 0' }}>
                    {(() => {
                        const pastMatches = matches.filter(m => m.is_past);
                        const latestMatch = pastMatches.length > 0
                            ? pastMatches.reduce((prev, curr) => new Date(prev.start_time) > new Date(curr.start_time) ? prev : curr)
                            : null;

                        return (
                            <Link
                                to={`/league/${leagueId}/create-proposal`}
                                state={{ baseMatch: latestMatch }}
                            >
                                <button className="primary-btn" style={{ width: '100%' }}>+ {t('propose_new_dates')}</button>
                            </Link>
                        );
                    })()}
                </div>
            )}

            <div className="section-header">
                <h2>{t('upcoming_matches')}</h2>
            </div>
            <div className="matches-container">
                {matches.filter(m => !m.is_past).length === 0 ? (
                    <p className="empty-msg">{t('no_upcoming_matches')}</p>
                ) : (
                    matches.filter(m => !m.is_past).map(match => {
                        const distance = match.user_coords && match.user_coords.lat && match.lat
                            ? calculateDistance(match.user_coords.lat, match.user_coords.lng, match.lat, match.lng)
                            : null;
                        const isCollapsed = collapsedMatchIds.has(match.id);

                        if (isCollapsed) {
                            return (
                                <div key={match.id} className="match-card collapsed" onClick={() => toggleMatchCollapse(match.id)} style={{ cursor: 'pointer', borderLeft: '4px solid #ccc', padding: '15px 24px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span style={{ color: '#888', fontSize: '0.9rem' }}>
                                            🚫 <strong>{t('not_participating')}:</strong> {new Date(match.start_time).toLocaleDateString()} {new Date(match.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </span>
                                        <span style={{ fontSize: '0.8rem', color: '#1976d2' }}>{t('expand_for_details')}</span>
                                    </div>
                                </div>
                            );
                        }

                        return (
                            <div key={match.id} className={`match-card card ${match.participants.some(p => String(p.user_id) === String(loggedUserId)) ? 'highlight-my-match' : ''}`}>
                                {match.user_not_available && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#ffebee', margin: '-24px -24px 15px -24px', padding: '10px 24px', borderBottom: '1px solid #ffcdd2' }}>
                                        <span style={{ color: '#c62828', fontWeight: 'bold', fontSize: '0.85rem' }}>{t('declared_not_available')}</span>
                                        <button onClick={(e) => { e.stopPropagation(); toggleMatchCollapse(match.id); }} className="secondary-btn" style={{ padding: '4px 8px', fontSize: '0.7rem' }}>{t('compact')}</button>
                                    </div>
                                )}
                                <div className="match-info">
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                        <strong>{new Date(match.start_time).toLocaleDateString()}</strong>
                                        <span>{new Date(match.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                        {match.participants.slice(0, match.max_players).some(p => String(p.user_id) === String(loggedUserId)) && (
                                            <span style={{ background: '#fff3e0', color: '#e65100', padding: '4px 8px', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 'bold' }}>{t('you_play')}</span>
                                        )}
                                        {match.participants.slice(match.max_players).some(p => String(p.user_id) === String(loggedUserId)) && (
                                            <span style={{ background: '#f5f5f5', color: '#616161', padding: '4px 8px', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 'bold' }}>⏳ {t('waitlist')}</span>
                                        )}
                                        {match.price && (
                                            <span style={{ fontSize: '0.85rem', color: '#666', background: '#f5f5f5', padding: '4px 8px', borderRadius: '4px' }}>
                                                💰 {parseFloat(match.price).toFixed(2)}€ (<strong>{(parseFloat(match.price) / match.max_players).toFixed(2)}€ {t('per_person')}</strong>)
                                            </span>
                                        )}
                                    </div>
                                    {match.location && (
                                        <div className="location-info">
                                            <a
                                                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(match.address ? `${match.location}, ${match.address}` : match.location)}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="location-link"
                                            >
                                                📍 {match.location}
                                                {match.address && <span className="address-text"> - {match.address}</span>}
                                            </a>
                                            {distance && (
                                                <span style={{ marginLeft: '10px', color: '#1976d2', fontWeight: 'bold', fontSize: '0.8rem', minWidth: '75px', textAlign: 'right' }}>
                                                    🚗 {distance} km
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <div className="participants-list">
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                        <h4 style={{ margin: 0 }}>
                                            <span>{t('participants')}: </span>
                                            {isAdmin ? (
                                                <span>
                                                    <input
                                                        type="text"
                                                        defaultValue={match.max_players}
                                                        onBlur={(e) => handleMaxPlayersChange(match.id, parseInt(e.target.value))}
                                                        style={{ width: '45px', padding: '2px 5px', borderRadius: '4px', border: '1px solid #ddd' }}
                                                    />
                                                    <span> {t('players')}</span>
                                                </span>
                                            ) : (
                                                <span>{match.max_players} {t('players')}</span>
                                            )}
                                        </h4>

                                        {isAdmin && (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <span
                                                    onClick={() => { if (match.is_teams_locked) { setRebalanceMatchId(match.id); setRebalanceModalOpen(true); } }}
                                                    style={{
                                                        fontSize: '0.75rem',
                                                        fontWeight: 'bold',
                                                        color: match.is_teams_locked ? '#ff9800' : '#4caf50',
                                                        cursor: match.is_teams_locked ? 'pointer' : 'default',
                                                        textDecoration: match.is_teams_locked ? 'underline' : 'none',
                                                        textUnderlineOffset: '3px'
                                                    }}
                                                >
                                                    {match.is_teams_locked ? t('lock_manual') : t('lock_auto')}
                                                </span>
                                                <label className="switch">
                                                    <input
                                                        type="checkbox"
                                                        checked={!match.is_teams_locked}
                                                        onChange={(e) => handleToggleTeamsLock(match.id, !e.target.checked)}
                                                        style={{ opacity: 0, width: 0, height: 0 }}
                                                    />
                                                    <span className="slider round" style={{ backgroundColor: !match.is_teams_locked ? '#4caf50' : '#ccc' }}></span>
                                                </label>
                                            </div>
                                        )}
                                    </div>

                                    {(() => {
                                        const confirmedPlayers = match.participants.slice(0, match.max_players);
                                        const whiteTeam = confirmedPlayers.filter(p => p.team === 'white');
                                        const blackTeam = confirmedPlayers.filter(p => p.team === 'black');
                                        const unassigned = confirmedPlayers.filter(p => !p.team);

                                        const whiteLevelSum = whiteTeam.reduce((sum, p) => sum + (p.level || 3), 0);
                                        const blackLevelSum = blackTeam.reduce((sum, p) => sum + (p.level || 3), 0);

                                        return (
                                            <>
                                                <div className={`pitch-container ${league?.sport || 'football'}`}>
                                                    <div className="pitch-line-center"></div>
                                                    <div className="pitch-line-circle"></div>

                                                    <div className="pitch-half white-side">
                                                        <div className="pitch-title">
                                                            {league?.sport === 'football' ? t('team_white') : t('team_a')}
                                                            {isAdmin && <span style={{ marginLeft: '10px', fontSize: '0.9rem', opacity: 0.8 }}>(Tot: {whiteLevelSum})</span>}
                                                        </div>
                                                        <div className="pitch-players">
                                                            {whiteTeam.map((p) => (
                                                                <div
                                                                    key={p.user_id || p.booking_id}
                                                                    className="pitch-player-card white"
                                                                    style={{ padding: '6px 10px', cursor: isAdmin ? 'pointer' : 'default' }}
                                                                    onClick={() => handleOpenTeamModal(match.id, p)}
                                                                >
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                        {p.roles && p.roles.length > 0 && (
                                                                            <div className="multi-role-badges" style={{ display: 'flex', gap: '2px' }}>
                                                                                {p.roles.map((r, i) => (
                                                                                    <div key={i} className={`role-badge ${r.charAt(0).toUpperCase()}`} title={t(r)}>
                                                                                        {getRoleBadge(r, t)}
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        )}
                                                                        <span>
                                                                            {p.is_external ? (
                                                                                <span style={{ fontStyle: 'italic', color: '#666' }}>
                                                                                    {p.external_name} ({t('external_label')})
                                                                                    {isAdmin && p.level && <small style={{ opacity: 0.7, fontSize: '0.65rem', marginLeft: '4px' }}>({p.level})</small>}
                                                                                </span>
                                                                            ) : (
                                                                                <>
                                                                                    {p.first_name.charAt(0).toUpperCase() + p.first_name.toLowerCase().slice(1)} {p.last_name && (p.last_name.charAt(0).toUpperCase() + '.')}
                                                                                    {p.is_guest && (
                                                                                        <>
                                                                                            <span title={t('scouted_player')} style={{ marginLeft: '5px', cursor: 'help' }}>🌟</span>
                                                                                            {isAdmin && p.reliability_score !== undefined && (
                                                                                                <small style={{ color: p.reliability_score < 50 ? '#d32f2f' : '#1976d2', fontWeight: 'bold', fontSize: '0.65rem' }}> ({p.reliability_score}%)</small>
                                                                                            )}
                                                                                        </>
                                                                                    )}
                                                                                    {isAdmin && p.level && <small style={{ opacity: 0.7, fontSize: '0.65rem', marginLeft: '4px' }}>({p.level})</small>}
                                                                                </>
                                                                            )}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>

                                                    <div className="pitch-half black-side">
                                                        <div className="pitch-title">
                                                            {league?.sport === 'football' ? t('team_black') : t('team_b')}
                                                            {isAdmin && <span style={{ marginLeft: '10px', fontSize: '0.9rem', opacity: 0.8 }}>(Tot: {blackLevelSum})</span>}
                                                        </div>
                                                        <div className="pitch-players">
                                                            {blackTeam.map((p) => (
                                                                <div
                                                                    key={p.user_id || p.booking_id}
                                                                    className="pitch-player-card black"
                                                                    style={{ padding: '6px 10px', cursor: isAdmin ? 'pointer' : 'default' }}
                                                                    onClick={() => handleOpenTeamModal(match.id, p)}
                                                                >
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'flex-end', width: '100%' }}>
                                                                        <span style={{ textAlign: 'right' }}>
                                                                            {p.is_external ? (
                                                                                <span style={{ fontStyle: 'italic', color: '#666' }}>
                                                                                    {p.external_name} (Esterno)
                                                                                    {isAdmin && p.level && <small style={{ opacity: 0.7, fontSize: '0.65rem', marginLeft: '4px' }}>({p.level})</small>}
                                                                                </span>
                                                                            ) : (
                                                                                <>
                                                                                    {p.is_guest && <span title={t('scouted_player')} style={{ marginRight: '5px', cursor: 'help' }}>🌟</span>}
                                                                                    {p.first_name.charAt(0).toUpperCase() + p.first_name.toLowerCase().slice(1)} {p.last_name && (p.last_name.charAt(0).toUpperCase() + '.')}
                                                                                    {p.level && <small style={{ opacity: 0.7, fontSize: '0.65rem', marginLeft: '4px' }}>({p.level})</small>}
                                                                                </>
                                                                            )}
                                                                        </span>
                                                                        {p.roles && p.roles.length > 0 && (
                                                                            <div className="multi-role-badges" style={{ display: 'flex', gap: '2px' }}>
                                                                                {p.roles.map((r, i) => (
                                                                                    <div key={i} className={`role-badge ${r.charAt(0).toUpperCase()}`} title={t(r)}>
                                                                                        {getRoleBadge(r, t)}
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>

                                                {unassigned.length > 0 && (
                                                    <div className="unassigned-bench">
                                                        <h4>{t('bench')}:</h4>
                                                        <div className="players-grid">
                                                            {unassigned.map((p) => (
                                                                <div
                                                                    key={p.user_id || p.booking_id}
                                                                    className="player-tag"
                                                                    style={{ cursor: isAdmin ? 'pointer' : 'default' }}
                                                                    onClick={() => handleOpenTeamModal(match.id, p)}
                                                                >
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                        {p.roles && p.roles.length > 0 && (
                                                                            <div className="multi-role-badges" style={{ display: 'flex', gap: '2px' }}>
                                                                                {p.roles.map((r, i) => (
                                                                                    <div key={i} className={`role-badge ${r.charAt(0).toUpperCase()}`} title={t(r)} style={{ width: '16px', height: '16px', fontSize: '9px' }}>
                                                                                        {getRoleBadge(r, t)}
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        )}
                                                                        <span>
                                                                            {p.is_external ? (
                                                                                <span style={{ fontStyle: 'italic', opacity: 0.8 }}>
                                                                                    {p.external_name} (Esterno)
                                                                                    {isAdmin && p.level && <small style={{ opacity: 0.7, fontSize: '0.7rem', marginLeft: '4px' }}>({p.level})</small>}
                                                                                </span>
                                                                            ) : (
                                                                                <>
                                                                                    {p.first_name.charAt(0).toUpperCase() + p.first_name.toLowerCase().slice(1)} {p.last_name && (p.last_name.charAt(0).toUpperCase() + '.')}
                                                                                    {p.is_guest && <span title={t('scouted_player')} style={{ marginLeft: '5px' }}>🌟</span>}
                                                                                    {isAdmin && p.level && <small style={{ opacity: 0.7, fontSize: '0.7rem', marginLeft: '4px' }}>({p.level})</small>}
                                                                                </>
                                                                            )}
                                                                        </span>
                                                                        {isAdmin && p.is_external && (
                                                                            <span
                                                                                onClick={(e) => { e.stopPropagation(); handleRemoveExternalPlayer(match.id, p.booking_id); }}
                                                                                style={{ cursor: 'pointer', marginLeft: '5px', fontSize: '0.8rem', color: '#ff5252' }}
                                                                                title={t('remove_external_player')}
                                                                            >🗑️</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                                <div style={{ display: 'flex', justifyContent: 'center' }}>
                                                    {isAdmin && (
                                                        <button
                                                            className="secondary-btn"
                                                            style={{ padding: '4px 8px', fontSize: '0.7rem', marginTop: '10px' }}
                                                            onClick={() => { setExternalPlayerSlotId(match.id); setExternalModalOpen(true); }}
                                                        >
                                                            {t('add_external_player')}
                                                        </button>
                                                    )}
                                                </div>

                                                {match.participants.length > match.max_players && (
                                                    <div className="waitlist" style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px dashed #ddd' }}>
                                                        <h4 style={{ margin: '0 0 10px 0', fontSize: '0.9rem', color: '#666' }}>{t('waitlist')}:</h4>
                                                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
                                                            {match.participants.slice(match.max_players).map((p, idx) => (
                                                                <div key={p.user_id || p.booking_id} className="player-tag waitlist">
                                                                    <span style={{ fontSize: '0.75rem' }}>{idx + 1}. </span>
                                                                    {p.is_external ? (
                                                                        <span style={{ fontStyle: 'italic' }}>
                                                                            {p.external_name} (Esterno)
                                                                            {isAdmin && p.level && <small style={{ opacity: 0.7, fontSize: '0.65rem', marginLeft: '4px' }}>({p.level})</small>}
                                                                        </span>
                                                                    ) : (
                                                                        <>
                                                                            {p.first_name.charAt(0).toUpperCase() + p.first_name.toLowerCase().slice(1)} {p.last_name && (p.last_name.charAt(0).toUpperCase() + '.')}
                                                                            {p.is_guest && <span title={t('scouted_player')} style={{ marginLeft: '5px' }}>🌟</span>}
                                                                        </>
                                                                    )}
                                                                    {isAdmin && p.is_external && (
                                                                        <span
                                                                            onClick={() => handleRemoveExternalPlayer(match.id, p.booking_id)}
                                                                            style={{ cursor: 'pointer', marginLeft: '5px', fontSize: '0.8rem', color: '#ff5252' }}
                                                                            title="Rimuovi iscritto esterno"
                                                                        >🗑️</span>
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        );
                                    })()}
                                </div>
                                <div className="match-actions">
                                    {memberStatus === 'approved' && (
                                        match.participants.some(p => String(p.user_id) === String(loggedUserId)) ? (
                                            <button
                                                onClick={() => handleRemoveBooking(match.id)}
                                                className="danger-btn"
                                            >
                                                {t('remove_availability')}
                                            </button>
                                        ) : (
                                            <button
                                                onClick={() => handleAddBooking(match.id)}
                                                className="primary-btn"
                                            >
                                                {t('add_availability')}
                                            </button>
                                        )
                                    )}
                                    {isAdmin && (
                                        <button
                                            onClick={() => handleCancelMatch(match.id)}
                                            className="secondary-btn"
                                        >
                                            📝 {t('edit_event')}
                                        </button>
                                    )}
                                    {isAdmin && (
                                        <button
                                            onClick={() => { setScoutingSlotId(match.id); setScoutingModalOpen(true); }}
                                            className="secondary-btn"
                                            style={{ border: '1px solid #4caf50', color: '#2e7d32' }}
                                        >
                                            📣 {t('call_guests')}
                                        </button>
                                    )}
                                </div>

                                {isAdmin && scoutingCandidates[match.id] && scoutingCandidates[match.id].length > 0 && (
                                    <div className="scouting-candidates-section" style={{ marginTop: '20px', padding: '15px', background: '#f1f8e9', borderRadius: '8px', border: '1px solid #c5e1a5' }}>
                                        <h4 style={{ margin: '0 0 10px 0', color: '#33691e', fontSize: '0.95rem' }}>🎯 Candidati Esterni Disponibili</h4>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                            {scoutingCandidates[match.id].map(cand => (
                                                <div key={cand.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fff', padding: '10px', borderRadius: '6px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                                                    <div>
                                                        <div style={{ fontWeight: 'bold' }}>{cand.first_name} {cand.last_name}</div>
                                                        <div style={{ fontSize: '0.8rem', color: '#666' }}>
                                                            Affidabilità: <span style={{ color: (cand.reliability_score || 100) < 50 ? '#d32f2f' : '#2e7d32', fontWeight: 'bold' }}>{cand.reliability_score || 100}%</span>
                                                            <span style={{ marginLeft: '10px' }}>Match: {cand.scout_matches_count || 0}</span>
                                                        </div>
                                                    </div>
                                                    <button 
                                                        onClick={() => handleAcceptCandidate(cand.id)}
                                                        className="primary-btn"
                                                        style={{ padding: '6px 12px', fontSize: '0.8rem', background: '#4caf50' }}
                                                    >
                                                        Accetta
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>

            {isAdmin && slots.some(s => s.reached_min_at !== null) && (
                <section className="ready-proposals-section" style={{ marginBottom: '30px' }}>
                    <div className="section-header" style={{ borderLeft: '4px solid #ff9800', paddingLeft: '15px' }}>
                        <h2 style={{ color: '#e65100' }}>⭐ {t('ready_proposals')}</h2>
                        <p className="subtitle">{t('ready_proposals_subtitle')}</p>
                    </div>
                    <div className="slots-container">
                        {slots
                            .filter(s => s.reached_min_at !== null)
                            .sort((a, b) => (a.priority_rank || 99) - (b.priority_rank || 99))
                            .map(slot => (
                                <SlotCard
                                    key={`ready-${slot.id}`}
                                    slot={slot}
                                    userIsAdmin={isAdmin}
                                    onVote={handleVote}
                                    onUnvote={handleUnvote}
                                    onConfirm={handleConfirmRequest}
                                    onDelete={handleDeleteRequest}
                                    onEdit={handleEditSlot}
                                    onNotAvailable={handleNotAvailable}
                                />
                            ))
                        }
                    </div>
                </section>
            )}

            {slots.length > 0 && (
                <section className="voting-section">
                    <div className="section-header">
                        <h2>{t('choose_date')}</h2>
                        <p className="subtitle">{t('options_voted_subtitle')}</p>
                    </div>

                    <div className="slots-container">
                        {slots.length === 0 ? (
                            <div className="empty-state card">
                                <p>{t('no_active_proposals')} {isAdmin ? t('create_first_league_now') : t('wait_for_admin')}</p>
                            </div>
                        ) : (
                            slots
                                .filter(slot => !isAdmin || slot.reached_min_at === null)
                                .map(slot => (
                                    <div key={slot.id} style={memberStatus !== 'approved' ? { opacity: 0.6, pointerEvents: 'none' } : {}}>
                                        <SlotCard
                                            slot={slot}
                                            userIsAdmin={isAdmin}
                                            onVote={handleVote}
                                            onUnvote={handleUnvote}
                                            onConfirm={handleConfirmRequest}
                                            onDelete={handleDeleteRequest}
                                            onEdit={handleEditSlot}
                                            onNotAvailable={handleNotAvailable}
                                        />
                                    </div>
                                ))
                        )}
                    </div>
                </section>
            )}

            {/* MATCH PASSATI */}
            <section className="matches-section">
                {matches.some(m => m.is_past) && (
                    <div className="past-matches-wrapper">
                        <button
                            className="secondary-btn toggle-past"
                            onClick={() => setShowPastMatches(!showPastMatches)}
                        >
                            {showPastMatches ? t('hide_past') : t('show_past')}
                        </button>
                        {showPastMatches && (
                            <div className="past-matches-list">
                                {matches.filter(m => m.is_past).map(match => (
                                    <div key={match.id} className="match-card past-card">
                                        <span>{new Date(match.start_time).toLocaleDateString()} - {match.participants.length} {t('players')}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </section>

            <ConfirmationModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onConfirm={handleConfirm}
                title={
                    modalAction === 'confirm' ? t('confirm_match') :
                        modalAction === 'cancelMatch' ? t('edit_event') :
                            modalAction === 'deleteProposal' ? t('delete_proposal') :
                                modalAction === 'vote' ? t('confirm_availability') :
                                    modalAction === 'unvote' ? t('remove_availability_modal') :
                                        modalAction === 'addBooking' ? t('confirm_participation') :
                                            modalAction === 'deleteLeague' ? t('delete') :
                                                modalAction === 'not_available' ? t('declare_not_available_modal') :
                                                    t('cancel_participation')
                }
                message={
                    modalAction === 'confirm' ? t('confirm_match_ask') :
                        modalAction === 'cancelMatch' ? t('cancel_match_modal_msg') :
                            modalAction === 'deleteProposal' ? t('delete_proposal_ask') :
                                modalAction === 'vote' ? t('vote_ask') :
                                    modalAction === 'unvote' ? t('unvote_ask') :
                                        modalAction === 'addBooking' ? t('add_booking_ask') :
                                            modalAction === 'deleteLeague' ? t('delete_league_ask') :
                                                modalAction === 'not_available' ? t('not_available_ask') :
                                                    t('remove_booking_ask')
                }
                showReasonInput={modalAction === 'cancelMatch' || modalAction === 'deleteProposal'}
            />

            <ConfirmationModal
                isOpen={alertModalConfig.isOpen}
                onClose={() => setAlertModalConfig({ isOpen: false, title: '', message: '' })}
                title={alertModalConfig.title}
                message={alertModalConfig.message}
                onCancel={() => setAlertModalConfig({ ...alertModalConfig, isOpen: false })}
                isAlert={true}
                noAutoClose={true}
            />

            {league && (
                <ConfirmationModal
                    isOpen={inviteModalOpen}
                    onClose={() => setInviteModalOpen(false)}
                    title={`🚀 ${t('invite_new_players')}`}
                    isAlert={true}
                    noAutoClose={true}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        <div style={{ background: '#f8f9fa', padding: '12px', borderRadius: '8px', border: '1px solid #eee' }}>
                            <p style={{ margin: '0 0 8px 0', fontSize: '0.85rem', color: '#666', fontWeight: 'bold' }}>{t('direct_link_to_league')}</p>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <input
                                    readOnly
                                    className="simple-input"
                                    value={`${window.location.origin}/league/${leagueId}?code=${league.invite_code}`}
                                    style={{ flex: 1, fontSize: '0.8rem', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                                />
                                <button
                                    onClick={() => copyToClipboard(`${window.location.origin}/league/${leagueId}?code=${league.invite_code}`, 'Link')}
                                    className="primary-btn"
                                    style={{ padding: '8px 12px', fontSize: '0.8rem' }}
                                >
                                    {t('copy')}
                                </button>
                            </div>
                            <p style={{ margin: '8px 0 0 0', fontSize: '0.75rem', color: '#888', fontStyle: 'italic' }}>
                                {t('link_explanation')}
                            </p>
                        </div>

                        <div style={{ textAlign: 'center', background: '#fff', padding: '15px', borderRadius: '8px', border: '1px solid #eee' }}>
                            <p style={{ margin: '0 0 10px 0', fontSize: '0.85rem', color: '#666', fontWeight: 'bold' }}>{t('scan_qr')}</p>
                            <img
                                src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(`${window.location.origin}/league/${leagueId}?code=${league.invite_code}`)}`}
                                alt="QR Code Invito"
                                style={{ width: '180px', height: '180px', margin: '0 auto', display: 'block' }}
                            />
                        </div>

                        <div style={{ background: '#f8f9fa', padding: '12px', borderRadius: '8px', border: '1px solid #eee' }}>
                            <p style={{ margin: '0 0 8px 0', fontSize: '0.85rem', color: '#666', fontWeight: 'bold' }}>{t('invite_code')}</p>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <code style={{ flex: 1, fontSize: '1.2rem', padding: '8px', background: '#fff', border: '1px solid #ccc', borderRadius: '4px', textAlign: 'center', fontWeight: 'bold', letterSpacing: '2px' }}>
                                    {league.invite_code}
                                </code>
                                <button
                                    onClick={() => copyToClipboard(league.invite_code, 'Codice')}
                                    className="primary-btn"
                                    style={{ padding: '8px 12px', fontSize: '0.8rem' }}
                                >
                                    {t('copy')}
                                </button>
                            </div>
                        </div>
                    </div>
                </ConfirmationModal>
            )
            }

            {/* Modal Modifica Slot (Proposta o Match) */}
            {
                isAdmin && editSlotModalOpen && slotToEdit && (
                    <ConfirmationModal
                        isOpen={editSlotModalOpen}
                        onClose={() => setEditSlotModalOpen(false)}
                        title={`📝 ${t('edit_option_match')}`}
                        isAlert={false}
                        hideDefaultActions={true}
                    >
                        <form onSubmit={handleUpdateSlot} style={{ display: 'flex', flexDirection: 'column', gap: '15px', textAlign: 'left' }}>
                            <div className="form-group">
                                <label style={{ fontWeight: 'bold' }}>{t('date_time')}</label>
                                <input
                                    type="datetime-local"
                                    value={slotToEdit.startTime}
                                    onChange={(e) => setSlotToEdit({ ...slotToEdit, startTime: e.target.value })}
                                    required
                                    style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ddd' }}
                                />
                            </div>
                            <div className="form-group">
                                <label style={{ fontWeight: 'bold' }}>{t('max_players')}</label>
                                <input
                                    type="number"
                                    value={slotToEdit.maxPlayers}
                                    onChange={(e) => handleEditFieldUpdate('maxPlayers', parseInt(e.target.value))}
                                    required
                                    style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ddd' }}
                                />
                            </div>
                            <div className="form-group suggestion-wrapper" style={{ position: 'relative' }}>
                                <label style={{ fontWeight: 'bold' }}>{t('location')}</label>
                                <input
                                    type="text"
                                    value={slotToEdit.location || ''}
                                    onFocus={() => setEditSuggestionOpen(true)}
                                    onBlur={() => setTimeout(() => setEditSuggestionOpen(false), 200)}
                                    onChange={(e) => handleEditFieldUpdate('location', e.target.value)}
                                    required
                                    style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ddd' }}
                                />
                                {editSuggestionOpen && previousLocations.length > 0 && (
                                    <div className="suggestions-dropdown" style={{ top: '100%', left: 0, right: 0 }}>
                                        {(() => {
                                            const uniqueLocations = [];
                                            const seen = new Set();
                                            previousLocations.forEach(loc => {
                                                if (!seen.has(loc.location.toLowerCase())) {
                                                    seen.add(loc.location.toLowerCase());
                                                    uniqueLocations.push(loc);
                                                }
                                            });
                                            return uniqueLocations
                                                .filter(loc => loc.location.toLowerCase().includes(slotToEdit.location?.toLowerCase() || ''))
                                                .map((loc, i) => (
                                                    <div
                                                        key={i}
                                                        className="suggestion-item"
                                                        onClick={() => handleEditSelectSuggestion(loc)}
                                                    >
                                                        <strong>{loc.location}</strong>
                                                        {loc.address && <span className="suggestion-address">{loc.address}</span>}
                                                    </div>
                                                ));
                                        })()}
                                    </div>
                                )}
                            </div>
                            <div className="form-group">
                                <label style={{ fontWeight: 'bold' }}>{t('address')}</label>
                                <input
                                    type="text"
                                    value={slotToEdit.address || ''}
                                    onChange={(e) => handleEditFieldUpdate('address', e.target.value)}
                                    onBlur={async () => {
                                        if (!slotToEdit.address) return;
                                        try {
                                            const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(slotToEdit.address)}&limit=1`);
                                            const data = await res.json();
                                            if (data && data.length > 0) {
                                                setSlotToEdit({
                                                    ...slotToEdit,
                                                    lat: parseFloat(data[0].lat),
                                                    lng: parseFloat(data[0].lon)
                                                });
                                            }
                                        } catch (err) { console.error(err); }
                                    }}
                                    style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ddd' }}
                                />
                            </div>
                            <div className="form-group">
                                <label style={{ fontWeight: 'bold' }}>Costo Affitto Totale (€)</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={slotToEdit.price || ''}
                                    onChange={(e) => handleEditFieldUpdate('price', e.target.value === '' ? null : parseFloat(e.target.value))}
                                    style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ddd' }}
                                />
                            </div>

                            <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                                <button type="submit" className="primary-btn" style={{ flex: 1 }}>{t('save_changes')}</button>
                                <button type="button" onClick={() => setEditSlotModalOpen(false)} className="secondary-btn" style={{ flex: 1 }}>{t('cancel')}</button>
                            </div>
                        </form>
                    </ConfirmationModal>
                )
            }

            {/* Modal Impostazioni Lega per Admin */}
            {
                isAdmin && (
                    <ConfirmationModal
                        isOpen={leagueModalOpen}
                        onClose={() => setLeagueModalOpen(false)}
                        title={`🔧 ${t('league_settings')}`}
                        isAlert={true}
                        noAutoClose={true}
                        hideDefaultActions={true}
                        disableOKButton={true}
                    >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', textAlign: 'left' }}>
                            <div className="form-group">
                                <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>{t('new_league')}</label>
                                <input
                                    value={editLeagueName}
                                    onChange={(e) => setEditLeagueName(e.target.value)}
                                    placeholder="Nome della lega"
                                    style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ddd' }}
                                />
                            </div>
                            <div className="form-group">
                                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={editLeaguePublic}
                                        onChange={(e) => setEditLeaguePublic(e.target.checked)}
                                        style={{ width: 'auto' }}
                                    />
                                    <span style={{ fontSize: '0.9rem' }}>{t('make_public')}</span>
                                </label>
                            </div>
                            <div className="form-group">
                                <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>{t('sport')}</label>
                                <select value={editSport} onChange={e => setEditSport(e.target.value)} className="simple-input" style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ddd' }}>
                                    <option value="football">⚽ {t('football')}</option>
                                    <option value="volleyball">🏐 {t('volleyball')}</option>
                                    <option value="padel">🎾 {t('padel')}</option>
                                    <option value="basketball">🏀 {t('basketball')}</option>
                                    <option value="tennis">🎾 {t('tennis')}</option>
                                    <option value="waterpolo">🤽 {t('waterpolo')}</option>
                                    <option value="rugby">🏉 {t('rugby')}</option>
                                </select>
                            </div>

                            <hr style={{ border: 'none', borderTop: '1px solid #eee', margin: '10px 0' }} />

                            <div className="form-group" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                    <span style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>{t('notifications_web_push')}</span>
                                </label>
                                <label className="switch" style={{ position: 'relative', display: 'inline-block', width: '46px', height: '24px' }}>
                                    <input
                                        type="checkbox"
                                        checked={league?.push_notifications_enabled}
                                        onChange={(e) => handleToggleMasterPush(e.target.checked)}
                                        style={{ opacity: 0, width: 0, height: 0 }}
                                    />
                                    <span style={{
                                        position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0,
                                        backgroundColor: league?.push_notifications_enabled ? 'var(--primary-color)' : '#ccc',
                                        transition: '.4s', borderRadius: '34px'
                                    }}>
                                        <span style={{
                                            position: 'absolute', content: '""', height: '18px', width: '18px', left: league?.push_notifications_enabled ? '24px' : '4px', bottom: '3px',
                                            backgroundColor: 'white', transition: '.4s', borderRadius: '50%'
                                        }}></span>
                                    </span>
                                </label>
                            </div>
                            <p style={{ margin: '-5px 0 10px 0', fontSize: '0.75rem', color: '#666' }}>
                                {t('notifications_master_desc')}
                            </p>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                <Link to={`/league/${leagueId}/members`} style={{ textDecoration: 'none' }}>
                                    <button className="secondary-btn" style={{ width: '100%', justifyContent: 'center' }}>{t('manage_participants')}</button>
                                </Link>

                                <button
                                    onClick={handleDeleteLeague}
                                    className="danger-btn"
                                    style={{ width: '100%', fontSize: '0.9rem', justifyContent: 'center' }}
                                >
                                    {t('delete_league_permanently')}
                                </button>
                            </div>

                            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                                <button onClick={handleSaveLeagueInfo} className="primary-btn" style={{ flex: 1, justifyContent: 'center' }}>{t('save')}</button>
                                <button onClick={() => setLeagueModalOpen(false)} className="secondary-btn" style={{ flex: 1, justifyContent: 'center' }}>{t('close_label')}</button>
                            </div>
                        </div>
                    </ConfirmationModal>
                )
            }
            {/* Modal Profilo */}
            {
                profileModalOpen && (
                    <ConfirmationModal
                        isOpen={profileModalOpen}
                        onClose={() => setProfileModalOpen(false)}
                        title={t('my_league_profile_title')}
                        hideDefaultActions={true}
                    >
                        <form onSubmit={handleUpdateProfile}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    <label style={{ fontWeight: 'bold', fontSize: '0.9rem' }}>{t('preferred_roles_label')}</label>
                                    <p style={{ fontSize: '0.75rem', color: '#666', margin: 0 }}>{t('roles_desc')}</p>
                                    <div className="role-menu-list" style={{ display: 'flex', flexDirection: 'column', gap: '2px', padding: '10px', background: '#fff', border: '1px solid #ddd', borderRadius: '8px', maxHeight: '180px', overflowY: 'auto' }}>
                                        {(SPORT_ROLES[league?.sport] || []).map(r => (
                                            <label key={r} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px', borderRadius: '6px', fontSize: '0.9rem', cursor: 'pointer', transition: 'background 0.2s' }} className="role-item">
                                                <input
                                                    type="checkbox"
                                                    style={{ width: '18px', height: '18px', flexShrink: 0 }}
                                                    checked={editProfileRoles.includes(r)}
                                                    onChange={e => {
                                                        const next = e.target.checked
                                                            ? [...editProfileRoles, r]
                                                            : editProfileRoles.filter(role => role !== r);
                                                        setEditProfileRoles(next);
                                                    }}
                                                />
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', justifyContent: 'space-between' }}>
                                                    <span style={{ fontWeight: editProfileRoles.includes(r) ? '600' : '400' }}>{t(r)}</span>
                                                    <div className={`role-badge ${r.charAt(0).toUpperCase()}`} style={{ minWidth: '20px', width: 'auto', padding: '0 4px', height: '20px', fontSize: '10px' }}>
                                                        {getRoleBadge(r, t)}
                                                    </div>
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                                    <button type="button" onClick={() => setProfileModalOpen(false)} className="outline-btn" style={{ flex: 1 }}>{t('close_label')}</button>
                                    <button type="submit" className="primary-btn" style={{ flex: 1 }}>{t('save_changes')}</button>
                                </div>
                            </div>
                        </form>
                    </ConfirmationModal>
                )
            }

            {
                teamModalOpen && (
                    <ConfirmationModal
                        isOpen={teamModalOpen}
                        onClose={() => setTeamModalOpen(false)}
                        title={teamPlayer?.is_external ? `${t('edit')} ${teamPlayer?.external_name}` : `${t('confirm_participation')} ${teamPlayer?.first_name}`}
                        hideDefaultActions={true}
                    >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                            {teamPlayer?.is_external && (
                                <div style={{ padding: '10px', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #eee', marginBottom: '5px' }}>
                                    <p style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#666', marginBottom: '8px', marginTop: 0 }}>{t('external_player_data')}</p>
                                    <input
                                        type="text"
                                        className="form-input"
                                        value={externalPlayerName}
                                        onChange={(e) => setExternalPlayerName(e.target.value)}
                                        placeholder={t('player_name_placeholder')}
                                        style={{ marginBottom: '10px', background: '#fff' }}
                                    />
                                    <div style={{ display: 'flex', gap: '5px', marginBottom: '10px' }}>
                                        {[1, 2, 3, 4, 5].map(lvl => (
                                            <button
                                                key={lvl}
                                                onClick={() => setExternalPlayerLevel(lvl)}
                                                style={{
                                                    flex: 1, padding: '5px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '0.8rem',
                                                    background: externalPlayerLevel === lvl ? '#1976d2' : '#fff',
                                                    color: externalPlayerLevel === lvl ? '#fff' : '#333'
                                                }}
                                            >
                                                {lvl}
                                            </button>
                                        ))}
                                    </div>
                                    <button
                                        className="primary-btn"
                                        style={{ width: '100%', padding: '8px', fontSize: '0.8rem' }}
                                        onClick={handleUpdateExternalPlayer}
                                    >
                                        {t('save_changes')}
                                    </button>
                                </div>
                            )}

                            <p style={{ margin: 0, fontSize: '0.9rem', color: '#666' }}>{t('choose_team')}</p>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <button
                                    className="secondary-btn"
                                    style={{ flex: 1, padding: '12px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}
                                    onClick={() => handleSetPlayerTeam('white')}
                                >
                                    <span style={{ fontSize: '1.2rem' }}>⚪️</span>
                                    <span style={{ fontSize: '0.8rem' }}>{t('team_white_short')}</span>
                                </button>
                                <button
                                    className="secondary-btn"
                                    style={{ flex: 1, padding: '12px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}
                                    onClick={() => handleSetPlayerTeam('black')}
                                >
                                    <span style={{ fontSize: '1.2rem' }}>⚫️</span>
                                    <span style={{ fontSize: '0.8rem' }}>{t('team_black_short')}</span>
                                </button>
                                <button
                                    className="outline-btn"
                                    style={{ flex: 1, padding: '12px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}
                                    onClick={() => handleSetPlayerTeam(null)}
                                >
                                    <span style={{ fontSize: '1.2rem' }}>🪑</span>
                                    <span style={{ fontSize: '0.8rem' }}>{t('bench')}</span>
                                </button>
                            </div>

                            {teamPlayer?.is_external && (
                                <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #eee' }}>
                                    <button
                                        className="danger-btn outline"
                                        style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', color: '#ff5252' }}
                                        onClick={() => { setTeamModalOpen(false); handleRemoveExternalPlayer(teamMatchId, teamPlayer.booking_id); }}
                                    >
                                        {t('remove_external_membership')}
                                    </button>
                                </div>
                            )}

                            <div style={{ marginTop: '5px' }}>
                                <button onClick={() => setTeamModalOpen(false)} className="secondary-btn" style={{ width: '100%', background: 'transparent', border: 'none', color: '#888' }}>{t('cancel')}</button>
                            </div>
                        </div>
                    </ConfirmationModal>
                )
            }
            {
                externalModalOpen && (
                    <ConfirmationModal
                        isOpen={externalModalOpen}
                        onClose={() => setExternalModalOpen(false)}
                        title={`➕ ${t('add_external_player')}`}
                        onConfirm={handleAddExternalPlayer}
                    >
                        <div style={{ textAlign: 'left' }}>
                            <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '15px' }}>
                                Inserisci il nome di una persona che parteciperà al match ma non ha un account su La Partitella.
                            </p>
                            <input
                                type="text"
                                className="form-input"
                                placeholder="Nome del giocatore"
                                value={externalPlayerName}
                                onChange={(e) => setExternalPlayerName(e.target.value)}
                                autoFocus
                                style={{ marginBottom: '15px' }}
                            />
                            <div style={{ marginBottom: '10px' }}>
                                <label style={{ display: 'block', fontSize: '0.85rem', color: '#666', marginBottom: '5px' }}>
                                    {t('level_desc')}:
                                </label>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    {[1, 2, 3, 4, 5].map(lvl => (
                                        <button
                                            key={lvl}
                                            type="button"
                                            onClick={() => setExternalPlayerLevel(lvl)}
                                            style={{
                                                flex: 1,
                                                padding: '8px',
                                                borderRadius: '6px',
                                                border: '1px solid #ddd',
                                                background: externalPlayerLevel === lvl ? '#1976d2' : '#fff',
                                                color: externalPlayerLevel === lvl ? '#fff' : '#333',
                                                cursor: 'pointer',
                                                fontWeight: 'bold',
                                                transition: 'all 0.2s'
                                            }}
                                        >
                                            {lvl}
                                        </button>
                                    ))}
                                </div>
                                <p style={{ fontSize: '0.7rem', color: '#888', marginTop: '5px', fontStyle: 'italic' }}>
                                    {externalPlayerLevel === 1 && t('level_1_desc')}
                                    {externalPlayerLevel === 2 && t('level_2_desc')}
                                    {externalPlayerLevel === 3 && t('level_3_desc')}
                                    {externalPlayerLevel === 4 && t('level_4_desc')}
                                    {externalPlayerLevel === 5 && t('level_5_desc')}
                                </p>
                            </div>
                        </div>
                    </ConfirmationModal>
                )
            }

            {/* Modale Bilanciamento Formazione */}
            {
                rebalanceModalOpen && (
                    <ConfirmationModal
                        isOpen={rebalanceModalOpen}
                        onClose={() => setRebalanceModalOpen(false)}
                        title={t('automation_management')}
                        hideDefaultActions={true}
                    >
                        <div style={{ textAlign: 'center', padding: '10px 0' }}>
                            <div style={{ fontSize: '3rem', marginBottom: '15px' }}>🤖</div>
                            <h3 style={{ marginBottom: '10px', color: '#333' }}>{t('manual_balancing_active')}</h3>
                            <p style={{ color: '#666', fontSize: '0.95rem', lineHeight: '1.5' }}>
                                {t('automation_desc')}
                            </p>
                            <div style={{ marginTop: '20px', padding: '12px', background: '#fff3e0', borderRadius: '10px', fontSize: '0.85rem', color: '#e65100', border: '1px solid #ffe0b2', textAlign: 'left' }}>
                                {t('automation_warning')}
                            </div>

                            <div style={{ marginTop: '25px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                <button
                                    onClick={() => {
                                        handleToggleTeamsLock(rebalanceMatchId, false);
                                        setRebalanceModalOpen(false);
                                    }}
                                    className="primary-btn"
                                    style={{ width: '100%', padding: '12px', background: '#4caf50', borderRadius: '8px' }}
                                >
                                    {t('automation_confirm')}
                                </button>
                                <button
                                    onClick={() => setRebalanceModalOpen(false)}
                                    className="secondary-btn"
                                    style={{ width: '100%', padding: '12px', borderRadius: '8px' }}
                                >
                                    {t('automation_cancel')}
                                </button>
                            </div>
                        </div>
                    </ConfirmationModal>
                )
            }
            {/* Modal Scouting */}
            {isAdmin && scoutingModalOpen && (
                <ConfirmationModal
                    isOpen={scoutingModalOpen}
                    onClose={() => setScoutingModalOpen(false)}
                    title={`📣 ${t('call_guests')}`}
                    isAlert={false}
                    hideDefaultActions={true}
                >
                    <form onSubmit={handleCreateScoutingRequest} style={{ display: 'flex', flexDirection: 'column', gap: '15px', textAlign: 'left' }}>
                        <div className="form-group">
                            <label style={{ fontWeight: 'bold' }}>{t('players_needed')}</label>
                            <input
                                type="number"
                                min="1"
                                max="20"
                                value={scoutingPlayers}
                                onChange={(e) => setScoutingPlayers(parseInt(e.target.value))}
                                required
                                style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ddd' }}
                            />
                        </div>
                        <div className="form-group">
                            <label style={{ fontWeight: 'bold' }}>{t('timeout_minutes')}</label>
                            <input
                                type="number"
                                min="1"
                                value={scoutingTimeout}
                                onChange={(e) => setScoutingTimeout(parseInt(e.target.value))}
                                required
                                style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ddd' }}
                            />
                             <small style={{ color: '#666' }}>{t('scouting_timeout_desc')}</small>
                        </div>
                        <div className="form-group">
                            <label style={{ fontWeight: 'bold' }}>{t('match_level')}</label>
                            <select 
                                value={scoutingLevel} 
                                onChange={(e) => setScoutingLevel(parseInt(e.target.value))}
                                style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ddd' }}
                            >
                                <option value={0}>{t('automatic_level')}</option>
                                <option value={1}>1 - {t('level_1_desc')}</option>
                                <option value={2}>2 - {t('level_2_desc')}</option>
                                <option value={3}>3 - {t('level_3_desc')}</option>
                                <option value={4}>4 - {t('level_4_desc')}</option>
                                <option value={5}>5 - {t('level_5_desc')}</option>
                            </select>
                            <small style={{ color: '#666' }}>{t('scouting_level_desc')}</small>
                        </div>
                        <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                            <button type="button" onClick={() => setScoutingModalOpen(false)} className="secondary-btn" style={{ flex: 1 }}>{t('cancel')}</button>
                            <button type="submit" className="primary-btn" style={{ flex: 1 }}>{t('confirm')}</button>
                        </div>
                    </form>
                </ConfirmationModal>
            )}
            {isAdmin && feedbackModalOpen && selectedScout && (
                <ConfirmationModal
                    isOpen={feedbackModalOpen}
                    onClose={() => setFeedbackModalOpen(false)}
                    title={`⭐ Feedback: ${selectedScout.user.first_name}`}
                    isAlert={false}
                    hideDefaultActions={true}
                >
                    <form onSubmit={handleSubmitFeedback} style={{ display: 'flex', flexDirection: 'column', gap: '15px', textAlign: 'left' }}>
                        <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <input 
                                type="checkbox" 
                                checked={feedbackData.wasPresent} 
                                onChange={(e) => setFeedbackData({ ...feedbackData, wasPresent: e.target.checked })}
                                id="wasPresent"
                            />
                            <label htmlFor="wasPresent" style={{ fontWeight: 'bold' }}>{t('was_present')}</label>
                        </div>
                        {feedbackData.wasPresent && (
                            <>
                                <div className="form-group">
                                    <label style={{ fontWeight: 'bold' }}>{t('rating')} (1-5)</label>
                                    <input 
                                        type="range" min="1" max="5" 
                                        value={feedbackData.rating} 
                                        onChange={(e) => setFeedbackData({ ...feedbackData, rating: parseInt(e.target.value) })}
                                        style={{ width: '100%' }}
                                    />
                                    <div style={{ textAlign: 'center', fontWeight: 'bold' }}>{feedbackData.rating} / 5</div>
                                </div>
                                <div className="form-group">
                                    <label style={{ fontWeight: 'bold' }}>{t('comment')}</label>
                                    <textarea 
                                        value={feedbackData.comment} 
                                        onChange={(e) => setFeedbackData({ ...feedbackData, comment: e.target.value })}
                                        style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ddd', minHeight: '80px' }}
                                        placeholder={t('comment_placeholder')}
                                    />
                                </div>
                            </>
                        )}
                        <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                            <button type="button" onClick={() => setFeedbackModalOpen(false)} className="secondary-btn" style={{ flex: 1 }}>{t('cancel')}</button>
                            <button type="submit" className="primary-btn" style={{ flex: 1 }}>{t('send_feedback')}</button>
                        </div>
                    </form>
                </ConfirmationModal>
            )}
        </div >
    );
};


export default LeagueDetail;
